import { Injectable, signal } from '@angular/core';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';

@Injectable()
export class DeliveryTrackerDetailUiStore {
  private readonly expandedTrackerIds = signal<Set<UUID>>(new Set());

  private readonly loadingTrackerIds = signal<Set<UUID>>(new Set());

  /**
   * Checks if a specific period is expanded.
   *
   * @param {UUID} trackerId - The tracker ID to check
   * @returns {boolean} true if the period is expanded
   */
  public isTrackerIdExpanded(trackerId: UUID): boolean {
    return this.expandedTrackerIds().has(trackerId);
  }

  /**
   * Sets expanded state for a specific tracker.
   *
   * @param {UUID} trackerId - The tracker ID
   * @param {boolean} isExpanded - Whether it should be expanded
   * @returns {void} This method does not return a value.
   */
  public setTrackerIdExpanded(trackerId: UUID, isExpanded: boolean): void {
    this.expandedTrackerIds.update((trackerIds) => {
      const newTrackerIds = new Set(trackerIds);
      if (isExpanded) {
        newTrackerIds.add(trackerId);
      } else {
        newTrackerIds.delete(trackerId);
      }
      return newTrackerIds;
    });
  }

  /**
   * Gets loading state for a specific tracker.
   *
   * @param {UUID} trackerId - The tracker ID
   * @returns {boolean} true if loading
   */
  public isLoadingByTracker(trackerId: UUID): boolean {
    return this.loadingTrackerIds().has(trackerId);
  }

  /**
   * Sets loading state for a specific tracker.
   *
   * @param {UUID} trackerId - The tracker ID
   * @param {boolean} isLoading - Loading state
   * @returns {void} This method does not return a value.
   */
  public setLoadingByTracker(trackerId: UUID, isLoading: boolean): void {
    this.loadingTrackerIds.update((ids) => {
      const newIds = new Set(ids);
      if (isLoading) {
        newIds.add(trackerId);
      } else {
        newIds.delete(trackerId);
      }
      return newIds;
    });
  }

  /**
   * Clears all UI state including expanded trackers and per-tracker loading states.
   *
   * @returns {void} This method does not return a value.
   */
  public clearAll(): void {
    this.expandedTrackerIds.set(new Set());
    this.loadingTrackerIds.set(new Set());
  }
}
